/**
 * Times of day as people say them ("2pm", "10:30 am", "18:00", "noon", "half
 * past 2", "11 in the morning"), read into the wizard's "HH:MM". An hour on
 * its own, with or without "half past"/"quarter to", could be either morning
 * or afternoon, so it's never read as a time without saying which.
 */

export const twoDigits = (n: number) => String(n).padStart(2, "0");

/** "am"/"pm", or a period of the day said in its place: "11 in the morning", "3 at night". */
const MERIDIEM_SOURCE = "am|pm|a\\.m\\.|p\\.m\\.|in the morning|in the afternoon|in the evening|at night";

/** Whether a MERIDIEM_SOURCE match means the afternoon or evening. */
function isAfternoon(meridiem: string): boolean {
  const lower = meridiem.toLowerCase();
  return lower.startsWith("p") || lower === "in the afternoon" || lower === "in the evening" || lower === "at night";
}

const HOUR_NAMES = ["one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve"];
const HOUR_SOURCE = `(?:\\d{1,2}|${HOUR_NAMES.join("|")})`;
const hourNumber = (text: string) => HOUR_NAMES.indexOf(text.toLowerCase()) >= 0 ? HOUR_NAMES.indexOf(text.toLowerCase()) + 1 : Number(text);

/** "half past", "quarter past" and "quarter to" an hour: the hour and minutes they mean. */
function relativeHourMinutes(phrase: string, hour: number): { hour: number; minutes: number } {
  const lower = phrase.toLowerCase();
  if (lower === "half past") return { hour, minutes: 30 };
  if (lower === "quarter past") return { hour, minutes: 15 };
  return { hour: hour === 1 ? 12 : hour - 1, minutes: 45 }; // quarter to
}

/**
 * "2pm", "6.30 p.m.", "14:15", "noon", "half past 2pm", "11 in the morning",
 * as one pattern; its groups are read by `timeOf`. "Half past"/"quarter to"
 * without am/pm is never matched here: that's ambiguous, and only handled as
 * a whole answer, by `timeAlone`.
 */
const TIME_SOURCE =
  `(?:\\b(half past|quarter past|quarter to)\\s+(${HOUR_SOURCE})\\s*(${MERIDIEM_SOURCE})(?![a-z])` +
  `|\\b(${HOUR_SOURCE})(?:[:.h](\\d{2}))?\\s*(${MERIDIEM_SOURCE})(?![a-z])` +
  `|\\b([01]?\\d|2[0-3])[:h]([0-5]\\d)\\b` +
  `|\\b(?:12\\s+)?(noon|midday)\\b)`;
const TIME = new RegExp(TIME_SOURCE, "i");

/** The time a match of TIME says, as "HH:MM", or null when it isn't one (13pm). */
function timeOf(match: RegExpMatchArray): string | null {
  const [, relPhrase, relHour, relMeridiem, hour12, minutes12 = "00", period, hour24, minutes24, noon] = match;
  if (noon) return "12:00";
  if (hour24) return `${twoDigits(Number(hour24))}:${minutes24}`;
  if (relPhrase) {
    const said = hourNumber(relHour);
    if (said < 1 || said > 12) return null;
    const { hour, minutes } = relativeHourMinutes(relPhrase, said);
    const afternoon = isAfternoon(relMeridiem);
    return `${twoDigits((hour % 12) + (afternoon ? 12 : 0))}:${twoDigits(minutes)}`;
  }
  const hour = hourNumber(hour12);
  const minutes = Number(minutes12);
  if (hour < 1 || hour > 12 || minutes > 59) return null;
  const afternoon = isAfternoon(period);
  return `${twoDigits((hour % 12) + (afternoon ? 12 : 0))}:${twoDigits(minutes)}`;
}

/** The first time said in the text, as "HH:MM", and where it was said. */
export function timeIn(text: string): { time: string; index: number; length: number } | null {
  const found = text.match(TIME);
  const time = found && timeOf(found);
  return time && found?.index !== undefined ? { time, index: found.index, length: found[0].length } : null;
}

/** Words before a time that only say it's a time: "at 2pm", "it starts at 2pm", "from 2pm". */
const BEFORE_A_TIME = /^(?:(?:it|that)\s+)?(?:(?:starts?|begins?|starting|kicks off)\s+)?(?:at|from|around|about)?\s*/i;

/** "half past 11", "quarter to 3", with no am/pm: the hour it's relative to could be either, so it's asked about, with its minutes kept. */
const RELATIVE_ALONE = new RegExp(`^(half past|quarter past|quarter to)\\s+(${HOUR_SOURCE})$`, "i");

/** An answer that's only a time, as "HH:MM"; "hour" when it's an hour on its own ("6", "half past 6"), which could be morning or afternoon. */
export function timeAlone(said: string): { time: string } | { hour: number; minutes: number } | null {
  const text = said.trim().replace(/[\s.!?]+$/, "").replace(/\s+sharp$/i, "").replace(BEFORE_A_TIME, "");
  const found = timeIn(text);
  if (found && found.index === 0 && found.length === text.length) return { time: found.time };
  const relative = text.match(RELATIVE_ALONE);
  if (relative) {
    const said12 = hourNumber(relative[2]);
    if (said12 < 1 || said12 > 12) return null;
    return relativeHourMinutes(relative[1], said12);
  }
  const hour = text.match(new RegExp(`^(${HOUR_SOURCE})(?:\\s*o'?clock)?$`, "i"));
  if (!hour) return null;
  const n = hourNumber(hour[1]);
  if (n >= 13 && n <= 23) return { time: `${twoDigits(n)}:00` };
  return n >= 1 && n <= 12 ? { hour: n, minutes: 0 } : null;
}

/** "14:00" as "2:00 PM", as the summary shows it. */
export function timeLabel(time: string): string {
  const [hour, minutes] = time.split(":").map(Number);
  return `${hour % 12 || 12}:${twoDigits(minutes)} ${hour < 12 ? "AM" : "PM"}`;
}
